import { useState, useEffect, useRef } from 'react'
import {
  Sparkle, MagicWand, ArrowLineDown, ArrowLineUp,
  Check, CursorClick, User, CircleNotch, CaretLeft,
} from '@phosphor-icons/react'
import { workflowsApi, getErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

interface AIWritingToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>
  value: string
  onChange: (value: string) => void
  fieldType: 'subject' | 'body'
}

// bodyOnly actions are hidden from the menu when fieldType is 'subject' — a
// subject line's only job is getting the email opened, not carrying the ask.
// The backend enforces the same rule (see BODY_ONLY_ACTIONS in
// workflows.py's ai_text_transform) so this is a UX filter, not the only
// guard: even a direct API call with field_type='subject' is rejected there.
const ACTIONS = [
  { action: 'improve', label: 'Improve writing', icon: MagicWand },
  { action: 'make_shorter', label: 'Make shorter', icon: ArrowLineDown },
  { action: 'make_longer', label: 'Make longer', icon: ArrowLineUp },
  { action: 'fix_grammar', label: 'Fix grammar & spelling', icon: Check },
  { action: 'add_cta', label: 'Add call-to-action', icon: CursorClick, bodyOnly: true },
  { action: 'personalize', label: 'Personalize with variables', icon: User },
] as const

const TONES = ['professional', 'friendly', 'casual', 'persuasive', 'urgent'] as const

// Success toast per action — can't derive this from the action name (naively
// appending 'd', e.g. 'fix_grammar' -> 'fix grammard', reads as broken as the
// bug it's meant to fix).
const ACTION_SUCCESS_MESSAGES: Record<string, string> = {
  improve: 'Writing improved',
  make_shorter: 'Text shortened',
  make_longer: 'Text lengthened',
  fix_grammar: 'Grammar and spelling fixed',
  add_cta: 'Call-to-action added',
  personalize: 'Personalized with variables',
  change_tone: 'Tone changed',
}

export default function AIWritingToolbar({ textareaRef, value, onChange, fieldType }: AIWritingToolbarProps) {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [toneOpen, setToneOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setToneOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAction = async (action: string, tone?: string) => {
    setOpen(false)
    setToneOpen(false)
    const el = textareaRef.current
    if (!el) return

    const start = el.selectionStart || 0
    const end = el.selectionEnd || value.length
    const selectedText = start !== end ? value.substring(start, end) : value

    if (!selectedText.trim()) {
      toast.error('No text to transform')
      return
    }

    const variableRegex = /\{\{[^}]+\}\}/g
    const availableVars = [...new Set(value.match(variableRegex) || [])]

    setIsLoading(true)
    try {
      const res = await workflowsApi.aiTransform({
        text: selectedText,
        action,
        tone,
        field_type: fieldType,
        available_variables: availableVars.length > 0 ? availableVars : undefined,
      })

      const transformed = res.data.transformed_text

      if (start !== end) {
        const newVal = value.substring(0, start) + transformed + value.substring(end)
        onChange(newVal)
      } else {
        onChange(transformed)
      }

      toast.success(ACTION_SUCCESS_MESSAGES[action] || 'Text updated')
    } catch (error) {
      toast.error(getErrorMessage(error, 'AI transform failed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={!value.trim() || isLoading}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-500/10 rounded-lg transition-all duration-150 disabled:opacity-50"
        title="AI writing assistant"
      >
        {isLoading
          ? <CircleNotch className="w-3.5 h-3.5 animate-spin" />
          : <Sparkle className="w-3.5 h-3.5" />
        }
        AI
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-56 bg-card border border-border/60 rounded-lg shadow-lg overflow-hidden">
          {ACTIONS.filter(a => fieldType === 'body' || !('bodyOnly' in a && a.bodyOnly)).map(({ action, label, icon: Icon }) => (
            <button
              key={action}
              type="button"
              onClick={() => handleAction(action)}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 hover:bg-primary/10 transition-all duration-150"
            >
              <Icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-foreground">{label}</span>
            </button>
          ))}

          {/* Tone submenu */}
          <div
            className="relative border-t border-border/40"
            onMouseEnter={() => setToneOpen(true)}
            onMouseLeave={() => setToneOpen(false)}
          >
            <button
              type="button"
              onClick={() => setToneOpen(!toneOpen)}
              className="w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-primary/10 transition-all duration-150"
            >
              <span className="flex items-center gap-2.5">
                <MagicWand className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-foreground">Change tone</span>
              </span>
              <CaretLeft className="w-3.5 h-3.5 text-muted-foreground" />
            </button>

            {toneOpen && (
              // Opens to the LEFT (right-full), not the right: this toolbar only
              // ever renders inside the Send Email node's config panel, a narrow
              // column pinned to the right edge of the screen (see the parent
              // dropdown's own right-0 anchor, chosen for the same reason). A
              // right-opening flyout stacks outward from an already right-edge
              // menu and renders off-viewport, making every tone option
              // invisible/unclickable (AB#21898).
              <div className="absolute right-full top-0 mr-1 w-40 bg-card border border-border/60 rounded-lg shadow-lg overflow-hidden">
                {TONES.map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    onClick={() => handleAction('change_tone', tone)}
                    className="w-full text-left px-3 py-2 text-sm text-foreground capitalize hover:bg-primary/10 transition-all duration-150"
                  >
                    {tone}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Credit hint */}
          <div className="px-3 py-1.5 bg-muted/50 border-t border-border/40">
            <span className="text-[10px] text-muted-foreground">~1 credit per transform</span>
          </div>
        </div>
      )}
    </div>
  )
}
